import React, { FC, useCallback, useEffect, useState } from 'react';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';
import InputClearIconButton from '../IconButton/InputClearIconButton';
import { useDebounceCallback } from '@react-hook/debounce';

interface TextFieldPropsPart {
  clearable?: boolean;
  debounce?: number;
}

export type TextFieldProps = TextFieldPropsPart &
  Omit<MuiTextFieldProps, keyof TextFieldPropsPart>;

const useStyles = makeStyles()(theme => ({
  root: {},
  endAdornment: {
    marginLeft: 4,
    display: 'flex',
    alignItems: 'center',
  },
}));

const TextField: FC<TextFieldProps> = ({
  InputProps,
  InputLabelProps,
  FormHelperTextProps,
  clearable,
  value,
  onChange,
  debounce = 0,
  classes,
  ...rest
}) => {
  const { classes: innerClasses, cx } = useStyles();

  const [innerValue, setInnerValue] = useState(value);
  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const debouncedOnChange = useDebounceCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onChange?.(e);
    },
    debounce,
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setInnerValue(e.target.value);
      debouncedOnChange(e);
    },
    [debouncedOnChange],
  );

  return (
    <MuiTextField
      {...rest}
      value={innerValue}
      onChange={handleChange}
      size="small"
      classes={{
        ...classes,
        root: cx(innerClasses.root, classes?.root),
      }}
      InputProps={{
        ...InputProps,
        endAdornment: clearable ? (
          <div className={innerClasses.endAdornment}>
            <InputClearIconButton
              hidden={!innerValue}
              onClick={() => {
                setInnerValue('');
                onChange?.({ target: { value: '' } } as any);
              }}
              disabled={rest.disabled}
            />
            {InputProps?.endAdornment}
          </div>
        ) : (
          InputProps?.endAdornment
        ),
      }}
      InputLabelProps={{
        ...InputLabelProps,
        classes: {
          ...InputLabelProps?.classes,
        },
        error: false,
      }}
      FormHelperTextProps={{
        ...FormHelperTextProps,
        classes: {
          ...FormHelperTextProps?.classes,
        },
      }}
    />
  );
};

export default TextField;
